import React, { useState } from 'react';
import { Card, Row, Col, Input, Button, message, Table, DatePicker, Select, Popconfirm } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import moment from 'moment';
import { getAlicuotasByRim, updateAlicuota, deleteAlicuota } from '../../../services/alicuota';

const toDateOnly = (value?: string): string => {
  if (!value) return '';
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : '';
};

interface AlicuotaEditable {
  id_actividad_economica_contribuyente: number;
  actividad_economica: string;
  numero_referencia: string;
  aplicable_desde: string;
  id_contribuyente: number;
  razon_social: string;
  rim: string;
}

const mockActividades = [
  { id_actividad_economica: 1, alicuota: 'Comercio', numero_referencia: 'A001' },
  { id_actividad_economica: 2, alicuota: 'Servicios', numero_referencia: 'A002' },
];

const ManageAlicuota: React.FC = () => {
  const [rim, setRim] = useState('');
  const [searching, setSearching] = useState(false);
  const [alicuotas, setAlicuotas] = useState<AlicuotaEditable[]>([]);
  const [editing, setEditing] = useState<{ [id: number]: Partial<AlicuotaEditable> }>({});
  const [actividades, setActividades] = useState(mockActividades);

  // Consulta de alícuotas por RIM
  const fetchAlicuotas = async (rim: string) => {
    try {
      const res = await getAlicuotasByRim(rim);
      setAlicuotas(res.alicuotas || []);
      setActividades(res.actividades_economicas || mockActividades);
      if (!res.alicuotas || res.alicuotas.length === 0) {
        message.info('No se encontraron alícuotas para el RIM ingresado.');
      } else {
        message.success('Alícuotas cargadas correctamente');
      }
    } catch (e) {
      
      message.error(e?.response?.data?.message || 'Error al consultar alícuotas');
      setAlicuotas([]);
    }
  };
  const handleSearch = () => {
    if (!rim) {
      message.warning('Debe ingresar el RIM del contribuyente');
      return;
    }
    setSearching(true);
    fetchAlicuotas(rim).then(() => setSearching(false));
  };

  const handleEdit = (id: number) => async () => {
    const original = alicuotas.find(a => a.id_actividad_economica_contribuyente === id);
    const numero_referencia = editing[id]?.numero_referencia ?? original?.numero_referencia ?? '';
    const aplicable_desde = editing[id]?.aplicable_desde ?? toDateOnly(original?.aplicable_desde) ?? '';
    if (!numero_referencia || !aplicable_desde) {
      message.warning('Debe completar todos los campos requeridos');
      return;
    }
    const data = {
      id_actividad: id,
      numero_referencia,
      aplicable_desde,
    };
    try {
      await updateAlicuota(data);
      message.success('Alícuota actualizada correctamente');
      setEditing(ed => ({ ...ed, [id]: {} }));
      fetchAlicuotas(rim);
    } catch (e) {
      
      message.error(e?.response?.data?.message || 'Error al actualizar alícuota');
    }
  };

  const handleDelete = (id: number) => async () => {
    try {
      await deleteAlicuota({ rim, id_actividad: id });
      setAlicuotas(alicuotas.filter(a => a.id_actividad_economica_contribuyente !== id));
      message.success('Alícuota eliminada correctamente');
      if (alicuotas.length === 1) {
        message.info('El contribuyente debe tener al menos una alícuota asignada');
      }
    } catch (e) {
      
      message.error(e?.response?.data?.message || 'Error al eliminar alícuota');
    }
  };

  const columns = [
    { title: 'Actividad Económica', dataIndex: 'actividad_economica', key: 'actividad_economica' },
    { title: 'Número Referencia', dataIndex: 'numero_referencia', key: 'numero_referencia',
      render: (_: string, record: AlicuotaEditable) => (
        <Select
          value={editing[record.id_actividad_economica_contribuyente]?.numero_referencia ?? record.numero_referencia}
          style={{ width: 140 }}
          onChange={val => setEditing(ed => ({ ...ed, [record.id_actividad_economica_contribuyente]: { ...ed[record.id_actividad_economica_contribuyente], numero_referencia: val } }))}
        >
          {actividades.map(a => <Select.Option key={a.numero_referencia} value={a.numero_referencia}>{a.alicuota} ({a.numero_referencia})</Select.Option>)}
        </Select>
      )
    },
    { title: 'Aplicable Desde', dataIndex: 'aplicable_desde', key: 'aplicable_desde',
      render: (_: string, record: AlicuotaEditable) => (
        (() => {
          const selectedDate = editing[record.id_actividad_economica_contribuyente]?.aplicable_desde;
          const currentDate = selectedDate || toDateOnly(record.aplicable_desde);
          return (
        <DatePicker
          value={currentDate ? moment(currentDate, 'YYYY-MM-DD') : null}
          format="YYYY-MM-DD"
          onChange={(_, dateString) => setEditing(ed => ({
            ...ed,
            [record.id_actividad_economica_contribuyente]: {
              ...ed[record.id_actividad_economica_contribuyente],
              aplicable_desde: String(dateString || ''),
            }
          }))}
        />
          );
        })()
      )
    },
    { title: 'Acciones', key: 'acciones',
      render: (_: any, record: AlicuotaEditable) => (
        <>
          <Button type="primary" onClick={handleEdit(record.id_actividad_economica_contribuyente)} style={{ marginRight: 8 }}>Guardar</Button>
          <Popconfirm title="¿Eliminar alícuota?" onConfirm={handleDelete(record.id_actividad_economica_contribuyente)} okText="Sí" cancelText="No">
            <Button danger>Eliminar</Button>
          </Popconfirm>
        </>
      )
    }
  ];

  return (
    <Card bodyStyle={{ padding: 0, overflowX: 'auto' }} style={{ overflowX: 'auto' }}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Input
            placeholder="RIM del contribuyente"
            value={rim}
            onChange={e => setRim(e.target.value)}
            onPressEnter={handleSearch}
          />
        </Col>
        <Col xs={24} md={4}>
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={searching}
            onClick={handleSearch}
          >
            Buscar Alícuotas
          </Button>
        </Col>
      </Row>
      <Table
        dataSource={alicuotas}
        columns={columns}
        rowKey="id_actividad_economica_contribuyente"
        pagination={false}
        locale={{ emptyText: 'Sin resultados' }}
        style={{ minWidth: 700 }}
      />


    </Card>
  );
}

export default ManageAlicuota;
